/**
 * IndexedDB: la memoria del móvil.
 *
 * Aquí está TODO lo que la empleada ha hecho y el servidor todavía no sabe.
 * Un piso sin cobertura, una limpieza entera grabada y el móvil apagándose por
 * batería son un martes cualquiera, así que la regla es:
 *
 *   NADA VIVE SOLO EN MEMORIA. Lo que se graba se escribe aquí antes de
 *   enseñar la siguiente pantalla.
 *
 * Y la contraria, igual de importante:
 *
 *   EL BLOB LOCAL SE BORRA SOLO CUANDO EL SERVIDOR CONFIRMA EL sha256.
 *   No cuando se manda el último trozo, ni cuando responde el `/complete`
 *   con 201: cuando la respuesta trae el `asset` con su hash. Es el contrato
 *   entero de la cadena de custodia visto desde el cliente.
 *
 * Los almacenes están pensados para trabajar con la app cerrada de golpe en
 * cualquier momento: cada uno se puede leer sin los demás y ninguno guarda
 * índices derivados que puedan quedar a medias.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

import { enIdioma, enIdiomaOpcional, idiomaDelNavegador } from '@haus/core';
import type {
    Calidad,
    DiaDeTrabajo,
    Empleada,
    Gravedad,
    Idioma,
    ModoEfectivo,
    Posicion,
    TipoAsset,
} from '@haus/core';

export type EstadoCola = 'pendiente' | 'subiendo' | 'hecha' | 'fallida';

/** Un clip o una foto esperando a subir. Solo los metadatos: ver `tiene_clip`. */
export interface EnCola {
    /** El asset_id que generó el móvil. Es la clave de idempotencia. */
    id: string;
    cleaning_id: string;
    cleaning_room_id: string | null;
    room_punto_id: string | null;
    apartamento: string;
    estancia: string;
    tipo: TipoAsset;
    mime: string;
    bytes: number;
    sha256: string;
    /**
     * ¿El fichero sigue en el móvil?
     *
     * El fichero NO vive aquí: vive en el almacén `clip`, con esta misma
     * clave. Estuvo dentro del registro y era un error de dos caras:
     *
     * - **Escritura.** Cada cambio de estado —empezar a subir, fallar,
     *   reintentar— reescribía el registro entero, o sea el vídeo entero. Un
     *   clip de 30 MB copiado en la flash del teléfono en cada intento.
     * - **Fiabilidad.** En WebKit, reescribir un registro con su Blob dentro
     *   falla a veces con «Error preparing Blob/File data to be stored in
     *   object store». Y el sitio donde eso reventaba era justo el peor: al
     *   apuntar que una subida había fallado. El clip se quedaba congelado en
     *   `subiendo`, sin contador de intentos, sin error que enseñar en la
     *   pantalla 08 y sin nadie que lo volviera a intentar. Es decir: el fallo
     *   que impedía registrar fallos.
     *
     * Separados, un cambio de estado escribe unos cientos de bytes y no toca
     * el fichero.
     */
    tiene_clip: boolean;
    capturado_at: string;
    duracion_ms: number | null;
    ancho: number | null;
    alto: number | null;
    calidad: Calidad | null;
    gps: Posicion | null;

    estado: EstadoCola;
    intentos: number;
    /** Cuándo volver a intentarlo. Es el backoff, en milisegundos de reloj. */
    reintentar_at: number;
    upload_id: string | null;
    chunk_bytes: number;
    /** Trozos que el servidor ya tiene. Se rellena con lo que él dice. */
    enviados: number[];
    ultimo_error: string | null;
    creado_at: number;
}

/** Una limpieza en curso, tal como la ve el móvil. */
export interface LimpiezaLocal {
    id: string;
    assignment_id: string | null;
    property_id: string;
    apartamento: string;
    modo: ModoEfectivo;
    started_at: string;
    ended_at: string | null;
    gps: Posicion | null;
    notas: string | null;
    /** ¿La conoce ya el servidor? Si no, la cola la crea antes de subir nada. */
    sincronizada: boolean;
    finalizada: boolean;
    estancias: EstanciaLocal[];
}

/**
 * Una estancia tal y como se guarda en el móvil.
 *
 * Los campos traducidos van PLANOS (`nombre_es`, `nombre_en`, `nombre_de`) y
 * no como un objeto `{es, en, de}`, aunque la API los mande así y el objeto
 * sea más cómodo. La razón es que esto vive en IndexedDB: cambiar la forma
 * dejaría los días ya cacheados sin el campo, y `enIdioma(e.nombre, …)` sobre
 * un `undefined` revienta **en el móvil de alguien que está en un piso sin
 * cobertura**, que es justo donde no se puede recuperar. Añadir un campo sí es
 * seguro: los registros viejos lo traen a `undefined` y el respaldo lo cubre.
 *
 * Se leen con {@link nombreEstancia}, {@link guiaEstancia} y
 * {@link etiquetaPunto}, nunca a mano.
 */
export interface EstanciaLocal {
    /** cleaning_room_id, generado en el móvil. */
    id: string;
    room_id: string;
    nombre_es: string;
    nombre_en: string | null;
    /** Opcional: el alemán llegó en 0022 y un día cacheado antes no lo trae. */
    nombre_de?: string | null;
    guia_es: string | null;
    guia_en: string | null;
    guia_de?: string | null;
    orden: number;
    min_fotos: number;
    min_video_s: number;
    max_video_s: number;
    puntos: {
        id: string;
        slug: string;
        etiqueta_es: string;
        etiqueta_en: string | null;
        etiqueta_de?: string | null;
    }[];
    estado: 'pendiente' | 'hecha' | 'no_accesible';
    motivo_no_accesible: string | null;
    video_ms: number | null;
    fotos: number;
}

/**
 * Los tres lectores de un campo traducido de una estancia cacheada.
 *
 * Existen para que no haya ni un `idioma === 'en' ? e.nombre_en : e.nombre_es`
 * suelto por las pantallas. Había cinco, uno por sitio, y ninguno falló al
 * añadir el alemán: un ternario binario sobre el idioma elige el español
 * cuando llega un tercero, sin error de compilación y sin aviso en consola.
 * La empleada veía la app en alemán y los nombres de las estancias en
 * español, que es justo lo que lee mientras graba.
 */
export function nombreEstancia(e: EstanciaLocal, idioma: Idioma): string {
    return enIdioma({ es: e.nombre_es, en: e.nombre_en, de: e.nombre_de }, idioma);
}

export function guiaEstancia(e: EstanciaLocal, idioma: Idioma): string | null {
    return enIdiomaOpcional({ es: e.guia_es, en: e.guia_en, de: e.guia_de }, idioma);
}

export function etiquetaPunto(p: EstanciaLocal['puntos'][number], idioma: Idioma): string {
    return enIdioma({ es: p.etiqueta_es, en: p.etiqueta_en, de: p.etiqueta_de }, idioma);
}

export interface IncidenciaLocal {
    id: string;
    cleaning_id: string;
    cleaning_room_id: string | null;
    texto: string;
    gravedad: Gravedad;
    sincronizada: boolean;
    creado_at: number;
}

export interface SesionGuardada {
    device_token: string;
    device_id: string;
    jwt: string;
    /** Milisegundos de reloj. Se compara con Date.now() para saber si caducó. */
    expira_at: number;
    empleada: Empleada;
    etiqueta_dispositivo: string;
}

export interface Ajustes {
    /**
     * El idioma de la app, que manda la oficina en la ficha de la empleada.
     *
     * Estaba escrito como `'es' | 'en'` en vez de `Idioma`, así que el día que
     * entró el alemán el compilador se plantó aquí: la ficha de una empleada
     * alemana llegaba con `de` y esta caja no lo admitía. Una unión copiada a
     * mano es una copia del tipo bueno que se queda vieja sin avisar.
     */
    idioma: Idioma;
    solo_wifi: boolean;
    /**
     * ¿Lo ha elegido la empleada, o sigue siendo el valor que puso oficina?
     *
     * `solo_wifi_por_defecto` es lo que su nombre dice: el valor INICIAL del
     * conmutador. Sin esta marca no hay forma de distinguir «está en no porque
     * nadie lo ha tocado» de «está en no porque ella lo apagó», y aplicar el
     * valor de oficina en cada día de trabajo le quitaría el conmutador de las
     * manos cada mañana.
     */
    solo_wifi_elegido: boolean;
    /**
     * La calidad de grabación. NO tiene marca de elección porque en la app no
     * hay ningún control para cambiarla: la decide la oficina, como el idioma.
     */
    calidad: Calidad;
}

interface EsquemaHaus extends DBSchema {
    cola: {
        key: string;
        value: EnCola;
        indexes: { 'por-estado': EstadoCola; 'por-limpieza': string };
    };
    limpiezas: {
        key: string;
        value: LimpiezaLocal;
    };
    incidencias: {
        key: string;
        value: IncidenciaLocal;
        indexes: { 'por-limpieza': string };
    };
    /** Un solo registro con la sesión, y otro con los ajustes. */
    clave_valor: {
        key: string;
        value: unknown;
    };
    /** El día de trabajo cacheado, para arrancar sin red. */
    dia: {
        key: string;
        value: { fecha: string; datos: DiaDeTrabajo; guardado_at: number };
    };
    /** El fichero de cada elemento de la cola, aparte de sus metadatos. */
    clip: {
        key: string;
        value: Blob;
    };
}

const NOMBRE = 'haus-limpiezas';
const VERSION = 2;

let promesa: Promise<IDBPDatabase<EsquemaHaus>> | null = null;

export function db(): Promise<IDBPDatabase<EsquemaHaus>> {
    promesa ??= openDB<EsquemaHaus>(NOMBRE, VERSION, {
        upgrade(base, desde, _hasta, tx) {
            if (desde < 1) {
                const cola = base.createObjectStore('cola', { keyPath: 'id' });
                cola.createIndex('por-estado', 'estado');
                cola.createIndex('por-limpieza', 'cleaning_id');

                base.createObjectStore('limpiezas', { keyPath: 'id' });

                const inc = base.createObjectStore('incidencias', { keyPath: 'id' });
                inc.createIndex('por-limpieza', 'cleaning_id');

                base.createObjectStore('clave_valor');
                base.createObjectStore('dia', { keyPath: 'fecha' });
            }

            if (desde < 2) {
                base.createObjectStore('clip');

                // Los clips que ya estuvieran en cola se mueven a su almacén.
                // No se puede vaciar la cola en una migración: ahí hay pruebas
                // de limpiezas que el servidor todavía no tiene.
                if (desde >= 1) {
                    void (async () => {
                        const cola = tx.objectStore('cola');
                        for (const viejo of await cola.getAll()) {
                            const conBlob = viejo as unknown as { blob?: Blob | null };
                            const blob = conBlob.blob ?? null;
                            if (blob !== null) {
                                await tx.objectStore('clip').put(blob, viejo.id);
                            }
                            delete conBlob.blob;
                            await cola.put({ ...viejo, tiene_clip: blob !== null });
                        }
                    })();
                }
            }
        },
        blocked() {
            // Otra pestaña con una versión vieja abierta. Con una PWA en un
            // móvil casi no pasa, pero si pasa hay que verlo.
            console.warn('[db] otra pestaña bloquea la actualización de la base local');
        },
        terminated() {
            // El navegador ha cerrado la conexión (iOS lo hace al quedarse sin
            // memoria). La siguiente llamada la reabre.
            promesa = null;
        },
    });

    return promesa;
}

// --- Sesión y ajustes ------------------------------------------------------

const CLAVE_SESION = 'sesion';
const CLAVE_AJUSTES = 'ajustes';

export async function leerSesion(): Promise<SesionGuardada | null> {
    return ((await (await db()).get('clave_valor', CLAVE_SESION)) as SesionGuardada) ?? null;
}

export async function guardarSesion(s: SesionGuardada): Promise<void> {
    await (await db()).put('clave_valor', s, CLAVE_SESION);
}

export async function borrarSesion(): Promise<void> {
    await (await db()).delete('clave_valor', CLAVE_SESION);
}

export const AJUSTES_POR_DEFECTO: Ajustes = {
    // El idioma del MÓVIL, no el castellano.
    //
    // Aquí ponía `'es'` fijo, y con eso el `?? idiomaDelNavegador()` de
    // `app.tsx` era código muerto: `leerAjustes()` siempre devolvía un idioma,
    // así que el `??` no saltaba nunca y `idiomaDelNavegador()` no se
    // ejecutaba en toda la aplicación. Un móvil recién puesto abría la app en
    // español dijera lo que dijera el sistema.
    //
    // Donde más se notaba es en la pantalla de ALTA, que es la primera que ve
    // una empleada y la única que se pinta antes de que exista ficha: con el
    // móvil en alemán salía entera en castellano. Comprobado en el despliegue
    // de verdad, y en inglés pasaba igual — o sea que llevaba ahí desde antes
    // del alemán.
    //
    // No lo cazó nadie porque **las cuatro pruebas del móvil fijan
    // `locale: 'es-ES'`**. Es la misma trampa que la del pie de la barra
    // lateral: se prueba con el idioma que uno tiene delante.
    //
    // Esto es solo el valor INICIAL. Lo que la empleada elija en el menú, y lo
    // que mande la oficina en su ficha al entrar, se guardan encima y ganan.
    idioma: idiomaDelNavegador(),
    solo_wifi: false,
    solo_wifi_elegido: false,
    // 720p es el valor de fabrica de `calidad_video` en la base, y esto es lo
    // que se usa hasta que llega el primer dia de trabajo con el de oficina.
    calidad: '720p',
};

export async function leerAjustes(): Promise<Ajustes> {
    const guardados = (await (await db()).get('clave_valor', CLAVE_AJUSTES)) as Ajustes | undefined;

    return { ...AJUSTES_POR_DEFECTO, ...(guardados ?? {}) };
}

export async function guardarAjustes(a: Ajustes): Promise<void> {
    await (await db()).put('clave_valor', a, CLAVE_AJUSTES);
}

// --- El día ----------------------------------------------------------------

export async function guardarDia(datos: DiaDeTrabajo): Promise<void> {
    const base = await db();
    await base.put('dia', { fecha: datos.fecha, datos, guardado_at: Date.now() });

    // Solo se conservan los tres últimos días. El del día anterior sirve para
    // ver lo que quedó subiendo anoche; el de hace un mes no sirve para nada y
    // ocupa espacio que hace falta para los vídeos.
    const claves = await base.getAllKeys('dia');
    const sobran = claves.sort().slice(0, Math.max(0, claves.length - 3));
    for (const k of sobran) {
        await base.delete('dia', k);
    }
}

export async function leerDia(fecha: string): Promise<DiaDeTrabajo | null> {
    const fila = await (await db()).get('dia', fecha);

    return fila?.datos ?? null;
}

/** El último día guardado, sea cual sea. Para arrancar sin red. */
export async function ultimoDia(): Promise<DiaDeTrabajo | null> {
    const base = await db();
    const claves = (await base.getAllKeys('dia')).sort();
    const ultima = claves[claves.length - 1];
    if (ultima === undefined) {
        return null;
    }

    return (await base.get('dia', ultima))?.datos ?? null;
}

// --- Limpiezas -------------------------------------------------------------

export async function guardarLimpieza(l: LimpiezaLocal): Promise<void> {
    await (await db()).put('limpiezas', l);
}

export async function leerLimpieza(id: string): Promise<LimpiezaLocal | null> {
    return (await (await db()).get('limpiezas', id)) ?? null;
}

/** La limpieza abierta, si hay una. Solo puede haber una a la vez. */
export async function limpiezaAbierta(): Promise<LimpiezaLocal | null> {
    const todas = await (await db()).getAll('limpiezas');

    return todas.find((l) => !l.finalizada) ?? null;
}

export async function limpiezasDeAsignacion(assignmentId: string): Promise<LimpiezaLocal[]> {
    const todas = await (await db()).getAll('limpiezas');

    return todas.filter((l) => l.assignment_id === assignmentId);
}

/**
 * Limpia las limpiezas finalizadas cuya cola ya está vacía.
 *
 * No se borran al finalizar: mientras quede un clip por subir, la fila local
 * es lo que permite reconstruir a qué limpieza pertenece.
 */
export async function purgarLimpiezasCompletas(): Promise<number> {
    const base = await db();
    const todas = await base.getAll('limpiezas');
    let borradas = 0;

    for (const l of todas) {
        if (!l.finalizada) {
            continue;
        }
        const enCola = await base.getAllFromIndex('cola', 'por-limpieza', l.id);
        if (enCola.every((c) => c.estado === 'hecha')) {
            for (const c of enCola) {
                await base.delete('cola', c.id);
            }
            await base.delete('limpiezas', l.id);
            borradas++;
        }
    }

    return borradas;
}

// --- Incidencias -----------------------------------------------------------

export async function guardarIncidencia(i: IncidenciaLocal): Promise<void> {
    await (await db()).put('incidencias', i);
}

export async function incidenciasPendientes(): Promise<IncidenciaLocal[]> {
    return (await (await db()).getAll('incidencias')).filter((i) => !i.sincronizada);
}

export async function incidenciasDe(cleaningId: string): Promise<IncidenciaLocal[]> {
    return (await db()).getAllFromIndex('incidencias', 'por-limpieza', cleaningId);
}

// --- Espacio ---------------------------------------------------------------

/** El fichero de un elemento de la cola. `null` si el móvil ya lo soltó. */
export async function leerClip(id: string): Promise<Blob | null> {
    return (await (await db()).get('clip', id)) ?? null;
}

export async function guardarClip(id: string, blob: Blob): Promise<void> {
    await (await db()).put('clip', blob, id);
}

/**
 * Suelta el fichero local.
 *
 * Solo se llama cuando el servidor ha confirmado el `sha256` del fichero
 * ensamblado. Antes, no: es la única copia que existe de esa prueba.
 */
export async function borrarClip(id: string): Promise<void> {
    await (await db()).delete('clip', id);
}

/**
 * Cuánto ocupa la cola y cuánto deja el móvil.
 *
 * El handoff habla de "límite del dispositivo 2 GB", pero 2 GB es una
 * suposición: la cuota real la da el navegador y depende del espacio libre del
 * teléfono. Se pregunta (decisión D9) en lugar de dar por hecho un número que
 * en un móvil lleno es falso y en uno vacío es pesimista.
 */
export async function espacio(): Promise<{ usado: number; cuota: number | null; pct: number }> {
    const base = await db();
    const cola = await base.getAll('cola');
    const usado = cola.reduce((t, c) => t + (c.tiene_clip ? c.bytes : 0), 0);

    let cuota: number | null = null;
    if (navigator.storage?.estimate !== undefined) {
        try {
            const e = await navigator.storage.estimate();
            cuota = e.quota ?? null;
        } catch {
            cuota = null;
        }
    }

    return { usado, cuota, pct: cuota === null || cuota === 0 ? 0 : (usado / cuota) * 100 };
}

/**
 * Pide almacenamiento persistente.
 *
 * Sin esto, el navegador puede tirar la base local cuando le falte espacio, y
 * con ella los vídeos que todavía no se han subido. Chrome lo concede sin
 * preguntar si la app está instalada; Safari no lo implementa, y ahí lo que
 * protege es subir cuanto antes.
 */
export async function pedirPersistencia(): Promise<boolean> {
    if (navigator.storage?.persist === undefined) {
        return false;
    }
    try {
        return await navigator.storage.persist();
    } catch {
        return false;
    }
}
